import { useEffect, useState } from "react";

import { ACCOUNT_STATUS } from "@shared/lib/types";
import { Avatar, Button, Modal, Row, Table, TableTitleLayout, Tag } from "@shared/ui";

import { PlusOutlined, RightOutlined } from "@ant-design/icons";
import { Col, Drawer, Flex, Input, Rate } from "antd";
import { ColumnsType } from "antd/es/table";
import styles from "./styles.module.scss";

import { ROUTES } from "@app/constants";
import { AgencyForm, useCreateAgency, useGetAgencies } from "@entities/agency";
import { AgencyDetails } from "@entities/agency/ui/AgencyDetails";
import { ToastService } from "@shared/lib/services";
import { Agency, CreateAgencyRequest } from "@shared/lib/types/agency";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import ColumnFilterWrapper from "@shared/ui/filters/ColumnFilterWrapper/ColumnFilterWrapper";
import { SelectedFilters } from "@shared/ui/filters/SelectedFilters";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function AgencyManagement() {
  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accountStatus, setAccountStatus] = useState<ACCOUNT_STATUS[]>([]);

  const [rating, setRating] = useState<number | undefined>(undefined);

  const [selectedAgency, setSelectedAgency] = useState<string>("");

  const { agencies, isFetching } = useGetAgencies(
    searchQuery,
    rating,
    accountStatus,
    pageSize,
    paegNo
  );

  const { createAgency, isCreating, isCreated } = useCreateAgency();

  const [isModalVisible, setModalVisible] = useState(false);

  const handleCreateBrand = () => {
    setSelectedAgency("");
    setModalVisible(true);
  };

  const handleSubmit = (brandData: Partial<Agency>) => {
    createAgency(brandData as CreateAgencyRequest);
  };
  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleRatingChange = (value: number | undefined) => {
    if (!value) return setRating(undefined);
    setRating(value);
  };
  const handleStatusChange = (value: ACCOUNT_STATUS[] | undefined) => {
    if (!value) return setAccountStatus([]);
    setAccountStatus(value);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  useEffect(() => {
    setPageNo(1);
  }, [searchQuery, rating, accountStatus, pageSize]);

  const statusToDisplay = Object.values(ACCOUNT_STATUS);

  const columns: ColumnsType<Agency> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: string, record: Agency) => (
        <Row
          align="middle"
          gutter={[8, 8]}
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate(ROUTES.AGENCY_INFLUENCER.replace(":id", record.agencyId))
          }
        >
          <Col>
            <Avatar
              src={record.profilePicUrl}
              alt="Profile Picture"
              size={40}
            />
          </Col>
          <Col>{record.name}</Col>
          <Col>
            <Button
              type="text"
              icon={<RightOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAgency(record.agencyId);
              }}
            />
          </Col>
        </Row>
      ),
    },

    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Rating",
      dataIndex: "avgReview",
      key: "avgReview",
      width: 200,
      filterCount: rating ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        const FilterDropdownContent = () => {
          const [tempRating, setTempRating] = useState<number>(rating || 0);

          return (
            <ColumnFilterWrapper
              title="Rating"
              filterCount={tempRating ? 1 : 0}
              isFiltered={tempRating > 0}
              onReset={() => {
                setTempRating(0);
                handleRatingChange(undefined);
                props.confirm();
              }}
              onApply={() => {
                handleRatingChange(tempRating);
                props.confirm();
              }}
              onClose={props.close}
            >
              <div style={{ padding: "8px 4px", display: "flex", justifyContent: "center" }}>
                <Rate
                  allowHalf
                  value={tempRating}
                  onChange={(value) => setTempRating(value)}
                />
              </div>
            </ColumnFilterWrapper>
          );
        };

        return <FilterDropdownContent />;
      },
      render: (avgReview: number) => (
        <Rate allowHalf disabled value={avgReview} />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filterCount: accountStatus.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Status"
          placeholder="Select Status"
          initialTagCount={3}
          itemsToDisplay={statusToDisplay}
          selectedItems={accountStatus}
          handleSelectionChange={(items) =>
            handleStatusChange(items as ACCOUNT_STATUS[])
          }
          props={props}
        />
      ),
      render: (status: ACCOUNT_STATUS) => (
        <Tag color={status === ACCOUNT_STATUS.ACTIVE ? "green" : "red"}>
          {status}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    if (isCreated) {
      ToastService.success("Agency Created");
      setModalVisible(false);
    }
  }, [isCreated]);

  return (
    <div>
      <h1 className={styles.title}>Agency</h1>

        <Table
          dataSource={agencies?.data}
          columns={columns}
          loading={isFetching}
          title={() => <TableTitleLayout 
            filterComponent={
              <div style={{width: "100%"}}>
            <Flex justify="space-between" style={{width: "100%"}}>
              <Input
            placeholder="Search by name of agency, company or employee"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            prefix={<Search size={16} />}
            style={{ width: 350 }}
          />
          <Button
            icon={<PlusOutlined />}
            type="primary"
            disabled={isCreating}
            onClick={() => handleCreateBrand()}
          >
            Create Agency
          </Button>
            </Flex>
            <SelectedFilters
          filters={{
            status: {
              keyvalue: accountStatus,
              clearHandler: () => handleStatusChange(undefined),
            },
          }}
        />
            </div>}
            totalText={`${agencies?.pagination?.totalCount || 0} Records found`}
          />}
          // rowKey="_id"
          pagination={{
            current: paegNo,
            pageSize: pageSize,
            total: agencies?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
          scroll={{
            x: "max-content"
          }}
        />
      <Modal
        title={"Create Agency"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <AgencyForm onSubmit={handleSubmit} isSubmitting={isCreating} />
      </Modal>
      <Drawer
        destroyOnClose
        title={"Agency"}
        open={Boolean(selectedAgency)}
        onClose={() => {
          setSelectedAgency("");
        }}
        footer={null}
        width={900}
      >
        {selectedAgency && <AgencyDetails agencyId={selectedAgency} />}
      </Drawer>
    </div>
  );
}
